import { useNavigate, useLocation } from "react-router-dom";
import { useDashLang } from "@/context/DashboardLangContext";
import { useAuth } from "@/context/AuthContext";
import type { PropertyType } from "@/lib/supabase/types";
import { canAccess } from "@/lib/rbac";

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  /** Mobile drawer open state. Ignored on desktop. */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

/**
 * Nav items are tagged with the module that owns them:
 *   - "hotel"  → only visible when the org has a hotel property
 *   - "rental" → only visible when the org has a rental property
 *   - "both"   → always visible (cross-module: guests, customers,
 *                billing, reports, staff, notifications, home)
 *
 * The nav filter is driven by `modules` on the AuthContext, which is
 * derived from the current org's `properties.type` values. So an org
 * that signed up with only `type = hotel` sees the hotel set; a
 * rental-only org sees the rental set; a mixed-group org sees both.
 */
type NavModule = PropertyType | "both";

type NavGroup = "home" | "frontdesk" | "operations" | "compliance" | "finance" | "admin";

interface NavItem {
  path: string;
  icon: string;
  en: string;
  ar: string;
  module: NavModule;
  group: NavGroup;
}

// Section headers, in display order. Items render under their group;
// empty groups (e.g. no rental module) are skipped.
const navGroups: Array<{ id: NavGroup; en: string; ar: string }> = [
  { id: "home",       en: "",            ar: "" },
  { id: "frontdesk",  en: "Front desk",  ar: "الاستقبال" },
  { id: "operations", en: "Operations",  ar: "التشغيل" },
  { id: "compliance", en: "Compliance",  ar: "الامتثال" },
  { id: "finance",    en: "Finance",     ar: "المالية" },
  { id: "admin",      en: "Admin",       ar: "الإدارة" },
];

const navItems: NavItem[] = [
  { path: "/dashboard",               icon: "ri-dashboard-line",      en: "Dashboard",      ar: "لوحة التحكم",    module: "both", group: "home" },

  // Hotel module
  { path: "/dashboard/checkin",       icon: "ri-user-received-line",  en: "Check-In",       ar: "تسجيل الوصول",    module: "hotel", group: "frontdesk" },
  { path: "/dashboard/walkin",        icon: "ri-walk-line",           en: "Walk-In",        ar: "ضيف مباشر",      module: "hotel", group: "frontdesk" },
  { path: "/dashboard/checkout",      icon: "ri-user-shared-line",    en: "Check-Out",      ar: "تسجيل المغادرة", module: "hotel", group: "frontdesk" },
  { path: "/dashboard/reservations",  icon: "ri-calendar-check-line", en: "Reservations",   ar: "الحجوزات",       module: "hotel", group: "frontdesk" },
  { path: "/dashboard/rooms",         icon: "ri-hotel-bed-line",      en: "Rooms",          ar: "الغرف",          module: "hotel", group: "operations" },
  { path: "/dashboard/housekeeping",  icon: "ri-brush-line",          en: "Housekeeping",   ar: "التدبير المنزلي", module: "hotel", group: "operations" },
  { path: "/dashboard/rasad",         icon: "ri-shield-check-line",   en: "Rasad",          ar: "رصد",            module: "hotel", group: "compliance" },

  // Rental module
  { path: "/dashboard/vehicles",      icon: "ri-car-line",            en: "Vehicles",       ar: "المركبات",       module: "rental", group: "operations" },
  { path: "/dashboard/new-rental",    icon: "ri-road-map-line",       en: "New Rental",     ar: "تأجير جديد",     module: "rental", group: "frontdesk" },
  { path: "/dashboard/vehicle-return",icon: "ri-parking-line",        en: "Vehicle Return", ar: "إعادة المركبة",  module: "rental", group: "frontdesk" },
  { path: "/dashboard/rental-reports",icon: "ri-line-chart-line",      en: "Rental Reports", ar: "تقارير التأجير", module: "rental", group: "finance" },

  // Cross-module (always on)
  // v1.22.4 — Guests and Customers are domain-specific:
  //   Guests   = hotel (reservations, nights, rooms)
  //   Customers = rental (vehicle rental agreements)
  // Previously both were tagged "both" which duplicated the same CRM
  // view under two labels for single-module tenants.
  { path: "/dashboard/guests",        icon: "ri-group-line",          en: "Guests",         ar: "النزلاء",       module: "hotel", group: "frontdesk" },
  { path: "/dashboard/customers",     icon: "ri-contacts-line",       en: "Customers",      ar: "العملاء",        module: "rental", group: "frontdesk" },
  { path: "/dashboard/billing",       icon: "ri-bill-line",           en: "Billing",        ar: "الفواتير",       module: "both", group: "finance" },
  // Reports is hotel-module specific (hotel occupancy, ADR, RevPAR,
  // etc.). Rental orgs get "Rental Reports" above. A mixed-group org
  // sees both entries side-by-side. v1.22.3 — was "both" which made
  // rental-only tenants see a dead "Hotel module not enabled" screen.
  { path: "/dashboard/reports",       icon: "ri-bar-chart-line",      en: "Hotel Reports",  ar: "تقارير الفندق",  module: "hotel", group: "finance" },
  { path: "/dashboard/staff",         icon: "ri-team-line",           en: "Staff",          ar: "الموظفون",      module: "both", group: "admin" },
  { path: "/dashboard/notifications", icon: "ri-notification-3-line", en: "Notifications",  ar: "الإشعارات",      module: "both", group: "admin" },
  { path: "/dashboard/batch-sync",    icon: "ri-refresh-line",        en: "Batch & Sync",   ar: "المزامنة",       module: "both", group: "compliance" },
  { path: "/dashboard/billing-settings", icon: "ri-wallet-3-line",    en: "Plan & Billing", ar: "الخطة والفوترة", module: "both", group: "admin" },
  { path: "/dashboard/activity",      icon: "ri-history-line",        en: "Activity Log",   ar: "سجل النشاط",    module: "both", group: "admin" },
];

export default function Sidebar({ collapsed, onToggle, mobileOpen = false, onMobileClose }: SidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAr, t } = useDashLang();
  const { hasHotel, hasRental, orgLoading, currentRole } = useAuth();

  // On mobile the drawer is always full-width — only desktop honours
  // the collapsed state. `showLabels` is the single derived flag the
  // JSX reads so we don't have to fork every conditional.
  const isEffectivelyCollapsed = collapsed;
  const showLabels = mobileOpen || !isEffectivelyCollapsed;
  const handleNavigate = (path: string) => {
    navigate(path);
    onMobileClose?.();
  };

  // Filter nav items by the tenant's enabled modules. While the org is
  // still loading we default to showing everything (avoids a flicker
  // where the nav disappears for a second on every page load).
  const visibleItems = navItems.filter((item) => {
    if (orgLoading) return true;
    // Module gate first (tenant has this module enabled).
    const moduleOk =
      item.module === "both"
        ? true
        : item.module === "hotel"
          ? hasHotel
          : item.module === "rental"
            ? hasRental
            : false;
    if (!moduleOk) return false;
    // Role gate — hide entries the current role can't access.
    // If we haven't resolved a role yet (brief paint during login),
    // keep the item visible to avoid a nav flicker.
    if (!currentRole) return true;
    return canAccess(currentRole, item.path);
  });

  // Desktop: normal flex child, width toggles between 64px and 190px.
  // Mobile: fixed-position drawer, full-height, slides in from edge.
  //   hidden by default (translate-x-full); `mobileOpen` slides it in.
  // Using `lg:` breakpoint because on md (tablet) the sidebar still
  // competes with main content too much.
  const edgeClass = isAr ? "right-0" : "left-0";
  const hiddenTransform = isAr ? "translate-x-full" : "-translate-x-full";

  return (
    <aside
      className={`
        bg-ink flex flex-col flex-shrink-0 transition-all duration-300 z-40
        fixed inset-y-0 ${edgeClass} w-64
        ${mobileOpen ? "translate-x-0" : hiddenTransform}
        lg:static lg:translate-x-0 lg:inset-auto
        ${isEffectivelyCollapsed ? "lg:w-16" : "lg:w-[190px]"}
      `}
      dir={isAr ? "rtl" : "ltr"}
    >
      {/* Logo */}
      <div className="h-14 flex items-center justify-between px-3 border-b border-white/10 flex-shrink-0">
        {showLabels && (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gold flex items-center justify-center flex-shrink-0">
              <span className="text-ink font-bold text-sm font-tajawal">ض</span>
            </div>
            <div>
              <span className="text-white font-bold text-sm font-tajawal">ضيافة</span>
              <span className="text-white/60 font-medium text-xs ms-1">Diyafa</span>
            </div>
          </div>
        )}
        {!showLabels && (
          <div className="w-8 h-8 rounded-lg bg-gold flex items-center justify-center mx-auto">
            <span className="text-ink font-bold text-sm font-tajawal">ض</span>
          </div>
        )}
        {/* Desktop fold/unfold — hidden on mobile where the scrim
         *  + tapping a nav item already close the drawer. */}
        {showLabels && (
          <button
            onClick={onToggle}
            className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/10 cursor-pointer transition-colors"
            aria-label={t("Collapse sidebar", "طي الشريط الجانبي")}
          >
            <i className={`text-base ${isAr ? "ri-menu-unfold-line" : "ri-menu-fold-line"}`}></i>
          </button>
        )}
        {/* Mobile close (X) — visible only on mobile inside the drawer. */}
        {mobileOpen && (
          <button
            onClick={onMobileClose}
            className="lg:hidden w-7 h-7 flex items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/10 cursor-pointer"
            aria-label={t("Close navigation", "إغلاق القائمة")}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        )}
      </div>

      {!showLabels && (
        <button
          onClick={onToggle}
          className="hidden lg:flex w-7 h-7 items-center justify-center rounded-md text-white/50 hover:text-white hover:bg-white/10 cursor-pointer transition-colors mx-auto mt-2"
          aria-label={t("Expand sidebar", "توسيع الشريط الجانبي")}
        >
          <i className={`text-base ${isAr ? "ri-menu-fold-line" : "ri-menu-unfold-line"}`}></i>
        </button>
      )}

      {/* Nav */}
      <nav className="flex-1 overflow-y-auto py-2 scrollbar-hide" aria-label={t("Main navigation", "التنقل الرئيسي")}>
        {navGroups.map((group) => {
          const items = visibleItems.filter((item) => item.group === group.id);
          if (items.length === 0) return null;
          return (
            <div key={group.id} role="group" aria-label={group.en ? t(group.en, group.ar) : undefined}>
              {group.en && (showLabels ? (
                <p className={`px-3 pt-4 pb-1 text-[10px] font-semibold uppercase tracking-wider text-white/60 ${isAr ? "font-tajawal" : ""}`}>
                  {t(group.en, group.ar)}
                </p>
              ) : (
                <div className="mx-3 my-2 border-t border-white/10" aria-hidden="true" />
              ))}
              {items.map((item) => {
                const active =
                  item.path === "/dashboard"
                    ? location.pathname === "/dashboard"
                    : location.pathname.startsWith(item.path);
                const label = isAr ? item.ar : item.en;
                return (
                  <button
                    key={item.path}
                    onClick={() => handleNavigate(item.path)}
                    title={collapsed ? label : undefined}
                    aria-label={showLabels ? undefined : label}
                    aria-current={active ? "page" : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 transition-all cursor-pointer text-start ${
                      active
                        ? "bg-gold/20 text-gold border-e-2 border-gold"
                        : "text-white/70 hover:bg-white/5 hover:text-white/90"
                    }`}
                  >
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${item.icon} text-base`} aria-hidden="true"></i>
                    </div>
                    {showLabels && (
                      <span className={`text-xs font-medium whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}>
                        {label}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Back to website */}
      <div className="border-t border-white/10 p-2">
        <button
          onClick={() => { navigate("/"); onMobileClose?.(); }}
          className={`w-full flex items-center gap-3 px-3 py-2.5 text-white/40 hover:text-white/70 hover:bg-white/5 cursor-pointer transition-colors rounded-lg`}
        >
          <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
            <i className={`text-base ${isAr ? "ri-arrow-right-line" : "ri-arrow-left-line"}`}></i>
          </div>
          {showLabels && (
            <span className={`text-xs font-medium whitespace-nowrap ${isAr ? "font-tajawal" : ""}`}>
              {t("Back to Website", "العودة للموقع")}
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}
